import React, { useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { css } from '@emotion/react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import { Airport } from '@/types/api/town-and-airports'
import { getArrowIcon } from '@/constants/arrowAssets'
import { useNavigationTracking } from '@/hooks/useNavigationTracking'

interface Props {
  airports: Airport[]
  onCancel: () => void
  isOpen: boolean
}

function HintAirportsBottomSheet({ airports, onCancel, isOpen }: Props) {
  const { trackClick } = useNavigationTracking()
  const router = useRouter()
  const { t } = useTranslation('common')
  const overlayRef = useRef<HTMLDivElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const handleAirportClick = (airport: any) => {
    trackClick(`main_page_search_hints_airport_${airport.code}`)
    onCancel()
    router.push({
      pathname: '/parking-search',
      query: {
        radius: 50000,
        service: 'PARK_AND_FLY',
        lat: airport.lat,
        lon: airport.lon,
        locationLabel: `${airport.code} - ${airport.cityName}`,
        cityUrlSlug: airport.cityUrlSlug
      }
    })
  }

  const getAirportHref = (airport: any) => {
    const params = new URLSearchParams({
      radius: '50000',
      service: 'PARK_AND_FLY',
      lat: airport.lat.toString(),
      lon: airport.lon.toString(),
      locationLabel: `${airport.code} - ${airport.cityName}`,
      cityUrlSlug: airport.cityUrlSlug
    })
    return `/parking-search?${params.toString()}`
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onCancel()
    }
  }

  if (!isOpen) return null

  return createPortal(
    <div css={overlayStyle} ref={overlayRef} onClick={handleOverlayClick}>
      <div
        css={sheetStyle}
        ref={sheetRef}
        onClick={e => {
          e.stopPropagation()
        }}
      >
        <div css={handleBarStyle} />
        <div css={contentStyle}>
          <h2 css={titleStyle}>{t('navigation.parkAndFly')}</h2>
          <div css={listStyle}>
            {airports.map((airport, index) => (
              <a
                key={`${airport.code}-${index}`}
                css={rowStyle}
                href={getAirportHref(airport)}
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleAirportClick(airport)
                }}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    handleAirportClick(airport)
                  }
                }}
              >
                <div css={airportStyle}>
                  <div css={shortcutStyle}>{airport.code}</div>
                  <div css={nameStyle}>{t(`${airport.name}` as any)}</div>
                </div>
                <div css={iconWrapperStyle}>
                  <img
                    src={getArrowIcon('blueChevron')}
                    alt="Arrow"
                    width={12}
                    height={12}
                    style={{ display: 'block' }}
                    data-testid="chevron-icon"
                  />
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>,
    typeof window !== 'undefined' ? document.body : (null as any)
  )
}

const overlayStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const sheetStyle = css`
  background: white;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  border-radius: 16px 16px 0 0;
  animation: slideUp 0.3s ease;
  overflow: hidden;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`

const handleBarStyle = css`
  width: 40px;
  height: 4px;
  background: #ccc;
  border-radius: 2px;
  margin: 12px auto 8px;
`

const contentStyle = css`
  padding: 0 20px 20px;
`

const titleStyle = css`
  margin: 0 0 24px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`

const listStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 60vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`

const rowStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  &:active {
    background-color: #e8f4fd;
  }

  &:focus {
    outline: 2px solid #3586e6;
    outline-offset: 2px;
  }
`

const airportStyle = css`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 8px;
`

const shortcutStyle = css`
  width: 48px;
  font-weight: 700;
  font-size: 14px;
  color: #3586e6;
`

const nameStyle = css`
  flex: 1;
  font-weight: 400;
  font-size: 14px;
  width: 244px;
  color: #3586e6;
  word-wrap: break-word;
`

const iconWrapperStyle = css`
  color: #3586e6;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
`

export default HintAirportsBottomSheet
